import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { submitBAARequest } from "services/baa";
import { INSTITUTION_TYPE_OPTIONS } from "./baaFormOptions";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_VALUES = {
  firstName: "",
  lastName: "",
  title: "",
  email: "",
  institutionName: "",
  institutionType: "",
  notes: "",
};

/**
 * Public, standalone institutional BAA request form. No authentication is
 * required. Drop it into any page; on success it replaces itself with a
 * confirmation message.
 *
 * @param {{ onSuccess?: (result: { requestId: string }, payload: object) => void }} props
 */
export default function BAARequestForm({ onSuccess }) {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ mode: "onTouched", defaultValues: DEFAULT_VALUES });

  const [submittedEmail, setSubmittedEmail] = useState(null);
  const [confirmationMode, setConfirmationMode] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const onSubmit = async (values) => {
    setSubmitError(null);
    const payload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      title: values.title.trim(),
      email: values.email.trim(),
      institutionName: values.institutionName.trim(),
      institutionType: values.institutionType,
      notes: values.notes ? values.notes.trim() : "",
    };
    try {
      const result = await submitBAARequest(payload);
      if (onSuccess) onSuccess(result, payload);
      if (result && result.signingUrl) {
        // Hand the user straight to the DocuSign signing ceremony.
        setRedirecting(true);
        window.location.href = result.signingUrl;
        return;
      }
      // Manual mode (owner prepares the BAA), or an auto-mode signing failure
      // where the owner/compliance team has been notified.
      setConfirmationMode(result && result.mode === "manual" ? "manual" : "failed");
      setSubmittedEmail(payload.email);
    } catch (error) {
      setSubmitError(
        (error && error.message) ||
          "Something went wrong submitting your request. Please try again."
      );
    }
  };

  if (redirecting) {
    return (
      <Stack spacing={2} alignItems="center" role="status" sx={{ py: 4 }}>
        <CircularProgress />
        <Typography color="text.secondary">
          Opening the secure signing form…
        </Typography>
      </Stack>
    );
  }

  if (submittedEmail) {
    return (
      <Stack spacing={2} alignItems="flex-start" role="status">
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckCircleOutlineIcon color="success" />
          <Typography variant="h6">Request received</Typography>
        </Stack>
        {confirmationMode === "manual" ? (
          <Typography color="text.secondary">
            Thanks — we&apos;ve received your request. A member of our team will
            prepare your Business Associate Agreement and email it to{" "}
            <strong>{submittedEmail}</strong> shortly to review and sign.
          </Typography>
        ) : (
          <Typography color="text.secondary">
            We&apos;ve received your request, but we couldn&apos;t open the
            signing form just now. Our team has been notified and will follow up
            with you at <strong>{submittedEmail}</strong> shortly.
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      aria-label="Business Associate Agreement request form"
    >
      <Stack spacing={2.5}>
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: "First name is required" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="First name"
                  fullWidth
                  required
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: "Last name is required" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Last name"
                  fullWidth
                  required
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        </Grid>

        <Controller
          name="title"
          control={control}
          rules={{ required: "Title or role is required" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Title / role"
              placeholder="Chief Compliance Officer"
              fullWidth
              required
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          rules={{
            required: "Work email address is required",
            pattern: {
              value: EMAIL_PATTERN,
              message: "Enter a valid email address",
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              type="email"
              label="Work email address"
              fullWidth
              required
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="institutionName"
          control={control}
          rules={{ required: "Institution name is required" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Institution name"
              fullWidth
              required
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="institutionType"
          control={control}
          rules={{ required: "Please choose an institution type" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              select
              label="Institution type"
              fullWidth
              required
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            >
              {INSTITUTION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Anything we should know before drafting?"
              placeholder="Optional"
              fullWidth
              multiline
              minRows={3}
            />
          )}
        />

        <Box>
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            loading={isSubmitting}
          >
            Request agreement
          </LoadingButton>
        </Box>
      </Stack>
    </Box>
  );
}
